import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import s from "./SubmissionRecord.module.scss";
import { exportCsv, queryList, splitSubmissionFile, submitFile } from "./model";

const SubmissionRecord = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
  });
  const [search, setSearch] = useState(false);
  const [columnsArray, setColumnsArray] = useState([]);

  useEffect(() => {
    setColumnsArray([...columns]);
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const initialFetch = async () => {
    setLoading(true);
    const apiResponse = await queryList({});
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setDatasource(responseBody);
          } else {
            snackbar(t("general.message.initial.fetch.error"), "error");
          }
        }
      }
    }
    setSearch(false);
    setLoading(false);
  };

  const splitSubmissionItem = async (data) => {
    const { settleDate } = data;
    const payload = { settleDate };
    const apiResponse = await splitSubmissionFile(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t(
                "page.finance.bankSubmission.innerTab.submissionRecord.splitSubmissionFileFunction.successMessage",
              ),
              "success",
            );
            return await initialFetch();
          } else {
            snackbar(
              t(
                "page.finance.bankSubmission.innerTab.submissionRecord.splitSubmissionFileFunction.failMessage",
              ),
              "error",
            );
            return;
          }
        }
      }
    }
  };

  const handleSubmit = async (data) => {
    const { settleDate } = data;
    const payload = { settleDate };
    const apiResponse = await submitFile(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t(
                "page.finance.bankSubmission.innerTab.submissionRecord.submitFunction.successMessage",
              ),
              "success",
            );
            return await initialFetch();
          } else {
            snackbar(
              t(
                "page.finance.bankSubmission.innerTab.submissionRecord.submitFunction.failMessage",
              ),
              "error",
            );
            return;
          }
        }
      }
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const apiResponse = await exportCsv();
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.export.function.success.message"),
              "success",
            );
            const tempDownloadAnchor = document.createElement("a");
            tempDownloadAnchor.style.display = "none";
            document.body.appendChild(tempDownloadAnchor);

            for (const item of responseBody.data) {
              tempDownloadAnchor.setAttribute("href", item.url);
              tempDownloadAnchor.setAttribute("download", item.filename);
              tempDownloadAnchor.click();
              // add delay to let it download file one by one (otherwise previous file will fail to download)
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
            document.body.removeChild(tempDownloadAnchor);
          } else {
            snackbar(
              t("general.message.export.function.fail.message"),
              "error",
            );
          }
        }
      }
    }
    setExporting(false);
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const columns = [
    {
      index: 0,
      key: "settleDate",
      title: t(
        "page.finance.bankSubmission.innerTab.submissionRecord.columnTitle.settlementDate",
      ),
      sorter: false,
      render: (data, key) => {
        return moment(data[key]).format("YYYY-MM-DD");
      },
    },
    {
      index: 1,
      key: "submitted",
      title: t(
        "page.finance.bankSubmission.innerTab.submissionRecord.columnTitle.submitted",
      ),
      sorter: false,
      render: (data, key) => {
        return data[key];
      },
    },
    {
      index: 2,
      key: "action",
      title: t(
        "page.finance.bankSubmission.innerTab.submissionRecord.columnTitle.action",
      ),
      sorter: false,
      render: (data) => {
        if (data.submitted === 0) {
          return (
            <div className={s.actionBtnContainer}>
              <button
                onClick={() => {
                  return splitSubmissionItem(data);
                }}
              >
                {t(
                  "page.finance.bankSubmission.innerTab.submissionRecord.splitSubmissionFileFunction.label",
                )}
              </button>
              <button
                onClick={() => {
                  return handleSubmit(data);
                }}
              >
                {t(
                  "page.finance.bankSubmission.innerTab.submissionRecord.submitFunction.label",
                )}
              </button>
            </div>
          );
        } else {
          return <div className={s.actionBtnContainer}></div>;
        }
      },
    },
  ];

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <FunctionalButtonBar
          loading={loading || exporting}
          exporting={exporting}
          exportBtn
          handleExport={handleExportCsv}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columnsArray}
              datasource={datasource.data}
              search={search}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(SubmissionRecord);
